import { Tooltip } from "antd";
import { Rotas } from "constants/EnumRotas";
import { TipoSituacaoProcessoJudicial, getSitucaoProcessoJudicial } from "constants/EnumTipoSituacaoProcessoJudicial";
import { BsFillEyeFill, BsFillPencilFill } from "react-icons/bs";
import { FaUndo } from "react-icons/fa";
import { FaFilePdf } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { LinhaTabela } from "shared/components/table/LinhaTabela";
import { RegistroTabela } from "shared/components/table/RegistroTabela";
import { RegistroProcessosJudiciaisDTO } from "types/dto/RegistroProcessosJudiciaisDTO";
import { RegistroTabelaProcessoJudicialDTO } from "types/dto/RegistroTabelaProcessoJudicialDTO";
import { useProcessosJudiciaisContext } from "context/gestaoCobranca/ProcessosJudiciaisContext";
import { queryClient } from "shared/util/queryClient";
import { useGetRelatorioProcessoJudicial } from "hooks/processosJudiciais/useGetRelatorioProcessoJudicial";
import { usePostCancelarProcessoJudicial } from "hooks/processosJudiciais/usePostCancelarProcessoJudicial";
import { useState } from "react";
import { useGetEditarVisualizarProcessoJudicial } from "hooks/processosJudiciais/useGetEditarVisualizarProcessoJudicial";
import showAlert from "shared/util/functions/alert/showAlert";
import { mensagemPadraoExclusao } from "shared/util/functions/alert/MensagensPadrõesAlerta";
import { possuiMensagemDeErro } from "shared/util/functions/errors/RequestPossuiMensagemDeErro";
import { toast } from "react-hot-toast";
import { getMensagemDeErroBackend } from "shared/util/functions/errors/GetMensagemDeErroBackend";

interface RenderRowsProcessosJudiciaisProps {
  registros: RegistroProcessosJudiciaisDTO[];
}

const RenderRowsProcessosJudiciais: React.FC<RenderRowsProcessosJudiciaisProps> = (props) => {
  const [processoId, setProcessoId] = useState<number | null>(null);
  const [idRelatorio, setIdRelatorio] = useState<number>();
  useGetEditarVisualizarProcessoJudicial(processoId);
  const { mutateAsync: postCancelarProcesso } = usePostCancelarProcessoJudicial();
  const { refetch } = useGetRelatorioProcessoJudicial(idRelatorio as number);

  const {
    possuiPermissaoEditarProcessosJudiciais,
    possuiPermissaoExcluirProcessosJudiciais,
    possuiPermissaoVisualizarProcessosJudiciais,
    possuiPermissaoVisualizarDetalhesProcesso,
  } = useProcessosJudiciaisContext();

  const navigate = useNavigate();

  const editarVisualizarProcessoJudicial = (dados: RegistroTabelaProcessoJudicialDTO, isVisualizacao: boolean) => {
    queryClient.invalidateQueries(["processos-judiciais-historicos"]);
    setProcessoId(Number.parseInt(dados.processoJudicialId));
    navigate(Rotas.PROCESSOS_JUDICIAIS, {
      state: {
        processoJudicialId: dados.processoJudicialId,
        isVisualizacao: isVisualizacao,
        isEdicao: !isVisualizacao,
        unidadeId: dados.unidadeId,
      },
    });
  };

  const cancelarProcessoJudicial = async (idProcesso: number) => {
    try {
      await postCancelarProcesso({ id: idProcesso });
    } catch (err) {
      if (possuiMensagemDeErro(err)) {
        toast.error(getMensagemDeErroBackend(err));
      } else {
        toast.error("Erro ao cancelar processo judicial");
      }
    }
  };

  const renderRegistros = () => {
    return props.registros.map((registro, index) => {
      return (
        <LinhaTabela id={`id-registro-processo-judicial-${index}`} key={`key-registro-processo-judicial-${index}`}>
          <RegistroTabela
            id={`nome-condominio-processos-judiciais-${registro.nomeCondominio}`}
            key={`key-nome-condominio-processos-judiciais-${registro.nomeCondominio}`}
          >
            <span>{registro.nomeCondominio}</span>
          </RegistroTabela>

          <RegistroTabela
            id={`numero-processo-processos-judiciais-${registro.numeroProcesso}`}
            key={`key-numero-processo-processos-judiciais-${registro.numeroProcesso}`}
          >
            <span>{registro.numeroProcesso}</span>
          </RegistroTabela>

          <RegistroTabela
            id={`unidade-sacado-processos-judiciais-${registro.nomeUnidadeSacado}`}
            key={`key-unidade-sacado-processos-judiciais-${registro.nomeUnidadeSacado}`}
          >
            <span>{registro.nomeUnidadeSacado}</span>
          </RegistroTabela>

          <RegistroTabela
            id={`situacao-processos-judiciais-${registro.situacao}`}
            key={`key-situacao-processos-judiciais-${registro.situacao}`}
          >
            <span>{getSitucaoProcessoJudicial(registro.situacao)}</span>
          </RegistroTabela>

          <RegistroTabela id={`acoes-processos-judiciais`} key={`acoes-processos-judiciais`}>
            {possuiPermissaoEditarProcessosJudiciais && registro.situacao != TipoSituacaoProcessoJudicial.CANCELADO && (
              <Tooltip placement="top" title="Editar">
                <a
                  onClick={() =>
                    editarVisualizarProcessoJudicial(registro as unknown as RegistroTabelaProcessoJudicialDTO, false)
                  }
                >
                  <BsFillPencilFill className="cursor-pointer me-2" size={15} color="#a69ae7" />
                </a>
              </Tooltip>
            )}

            {possuiPermissaoVisualizarProcessosJudiciais && (
              <Tooltip placement="top" title="Visualizar">
                <a
                  onClick={() =>
                    editarVisualizarProcessoJudicial(registro as unknown as RegistroTabelaProcessoJudicialDTO, true)
                  }
                >
                  <BsFillEyeFill className="cursor-pointer me-2" size={15} color="#a69ae7" />
                </a>
              </Tooltip>
            )}

            {possuiPermissaoExcluirProcessosJudiciais &&
              registro.situacao != TipoSituacaoProcessoJudicial.CANCELADO &&
              registro.situacao != TipoSituacaoProcessoJudicial.PROCESSO_FINALIZADO && (
                <Tooltip placement="top" title="Cancelar processo">
                  <a
                    onClick={() => {
                      showAlert({
                        texto: "Você realmente deseja cancelar o processo?",
                        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
                        onConfirm: () => {
                          cancelarProcessoJudicial(registro.processoJudicialId);
                        },
                      });
                    }}
                  >
                    <FaUndo className="cursor-pointer me-2" size={14} color="#a69ae7" />
                  </a>
                </Tooltip>
              )}
            {possuiPermissaoVisualizarDetalhesProcesso && (
              <Tooltip placement="top" title="Emitir detalhes do processo">
                <a
                  onClick={() => {
                    setIdRelatorio(registro.processoJudicialId);
                    refetch();
                  }}
                >
                  <FaFilePdf className="cursor-pointer me-2" size={15} color="#a69ae7" />
                </a>
              </Tooltip>
            )}
          </RegistroTabela>
        </LinhaTabela>
      );
    });
  };

  return <>{renderRegistros()}</>;
};

export default RenderRowsProcessosJudiciais;
